import './coapplicantPreviousEmploymentPage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, touchedFields, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    computed: { isCoapplicantPersisted },
    coapplicantPreviousIncomeEmployerName,
    coapplicantPreviousIncomeEmploymentType,
    coapplicantPreviousIncomeGrossAnnual,
    coapplicantPreviousIncomeJobTitle,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantPreviousIncomeEmployerName,
    setCoapplicantPreviousIncomeEmploymentType,
    setCoapplicantPreviousIncomeGrossAnnual,
    setCoapplicantPreviousIncomeJobTitle,
    syncCoapplicant,
  } = useCoapplicantStore();

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldShowEmployedFields = employedTypes.includes(
    watch(
      'previousEmploymentTypeValue',
      coapplicantPreviousIncomeEmploymentType,
    ),
  );

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),

    onChange: (): void => {
      // the employmentType selection determines whether the employerName and jobTitle fields are rendered.
      // because the fields are conditionally required, we only revalidate the necessary form fields.
      const fieldsToRevalidate = shouldShowEmployedFields
        ? [
            'previousEmployerNameValue',
            'previousJobTitleValue',
            'previousGrossAnnualIncomeValue',
          ]
        : ['previousGrossAnnualIncomeValue'];

      // only revalidate the fields that have been touched by the user as we normally validate onBlur.
      // this is to prevent the form from showing errors before the user has interacted with the form.
      fieldsToRevalidate
        .filter((field) => touchedFields[field])
        .forEach((field) => {
          trigger(field);
        });
    },
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      previousEmploymentTypeValue,
      previousEmployerNameValue,
      previousJobTitleValue,
      previousGrossAnnualIncomeValue,
    } = values;

    if (
      !previousEmploymentTypeValue ||
      (shouldShowEmployedFields && !previousEmployerNameValue) ||
      (shouldShowEmployedFields && !previousJobTitleValue) ||
      !previousGrossAnnualIncomeValue
    ) {
      return;
    }

    setCoapplicantPreviousIncomeEmploymentType(previousEmploymentTypeValue);
    setCoapplicantPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);

    if (shouldShowEmployedFields) {
      setCoapplicantPreviousIncomeEmployerName(previousEmployerNameValue);
      setCoapplicantPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setCoapplicantPreviousIncomeEmployerName(null);
      setCoapplicantPreviousIncomeJobTitle(null);
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-previous-employment-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantPreviousEmployment.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantPreviousEmployment.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantPreviousEmployment.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantPreviousEmployment.progress.root
            }
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantPreviousEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantPreviousEmployment.form.h1
                }
              >
                {t('CoapplicantPreviousEmploymentPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantPreviousEmployment.form.h2
                }
              >
                {t('CoapplicantPreviousEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPreviousEmployment.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'previousEmploymentTypeValue',
                      employmentTypeValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    autoComplete="address-level1"
                    error={!isEmpty(errors.previousEmploymentTypeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantPreviousEmployment.form
                          .employmentTypeSelect,
                    }}
                    label={t(
                      'CoapplicantPreviousEmploymentPage.employmentType',
                    )}
                    value={watch(
                      'previousEmploymentTypeValue',
                      coapplicantPreviousIncomeEmploymentType || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantPreviousEmploymentPage.employmentType')}
                    </MenuItem>

                    {previousEmploymentTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.previousEmploymentTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantPreviousEmployment.form
                          .employmentTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.previousEmploymentTypeValue.message)}
                    </span>
                  )}
                </div>

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'previousEmployerNameValue',
                        employerNameValidation,
                      )}
                      defaultValue={coapplicantPreviousIncomeEmployerName}
                      error={!isEmpty(errors.previousEmployerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantPreviousEmployment.form
                            .employerNameInput,
                      }}
                      label={t(
                        'CoapplicantPreviousEmploymentPage.employerName',
                      )}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.previousEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantPreviousEmployment.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('previousJobTitleValue', jobTitleValidation)}
                      defaultValue={coapplicantPreviousIncomeJobTitle}
                      error={!isEmpty(errors.previousJobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantPreviousEmployment.form
                            .jobTitleInput,
                      }}
                      label={t('CoapplicantPreviousEmploymentPage.jobTitle')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.previousJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantPreviousEmployment.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'previousGrossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    defaultValue={inputEventHandler.currency.formatter(
                      coapplicantPreviousIncomeGrossAnnual,
                    )}
                    error={!isEmpty(errors.previousGrossAnnualIncomeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantPreviousEmployment.form
                          .grossAnnualIncomeInput,
                      inputMode: 'numeric',
                    }}
                    label={t(
                      'CoapplicantPreviousEmploymentPage.grossAnnualIncome',
                    )}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.previousGrossAnnualIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantPreviousEmployment.form
                          .grossAnnualIncomeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.previousGrossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantPreviousEmployment.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantPreviousEmploymentPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantPreviousEmployment.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPreviousEmploymentPage';
